import React, { useCallback, useEffect, useState } from 'react';
import Loading from '../../components/common/Loading/Loading';
import FormSelect from '../../components/common/FormSelect/FormSelect';
import FormInput from '../../components/common/FormInput/FormInput';
import { SubscriptionPlanService } from '../../services';
import { CheckoutLinkTrackingService } from '../../services/CheckoutLinkTrackingService';
import { SubscriptionPlan } from '../../types';
import { formatCurrency } from '../../utils';
import { CYCLE_LABELS } from '../../components/students/PaymentDetails/paymentDetails.constants';
import Tooltip from '../../components/common/Tooltip';
import { createPlanSelectFormatters } from '../../components/common/PlanNameWithProject/PlanNameWithProject';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';
import styles from './CommercialCheckoutLinkModal.module.css';

const CHECKOUT_ALREADY_COMPLETED_MESSAGE = 'Checkout já concluído.';

interface CommercialCheckoutLinkModalProps {
  open: boolean;
  leadName: string;
  studentId?: number | null;
  qualificationId?: number | null;
  initialPlanId?: number | null;
  onClose: () => void;
}

const CommercialCheckoutLinkModal: React.FC<CommercialCheckoutLinkModalProps> = ({
  open,
  leadName,
  studentId = null,
  qualificationId = null,
  initialPlanId = null,
  onClose,
}) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [checkoutAlreadyCompleted, setCheckoutAlreadyCompleted] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedPlanId('');
      setCheckoutUrl('');
      setCheckoutAlreadyCompleted(false);
      setIsCopied(false);
      return;
    }

    setPlansLoading(true);
    SubscriptionPlanService.getSelectablePlans()
      .then((loadedPlans) => {
        setPlans(loadedPlans);
        if (initialPlanId && loadedPlans.some((plan) => plan.id === initialPlanId)) {
          setSelectedPlanId(String(initialPlanId));
        }
      })
      .finally(() => setPlansLoading(false));
  }, [initialPlanId, open]);

  useEffect(() => {
    setCheckoutUrl('');
    setCheckoutAlreadyCompleted(false);
    setIsCopied(false);
  }, [open, selectedPlanId]);

  useEscapeToClose(onClose, open);

  const handleCopy = useCallback(async () => {
    const planId = Number(selectedPlanId);
    if (!Number.isInteger(planId) || planId <= 0 || isCopying) {
      return;
    }

    setIsCopying(true);
    setCheckoutAlreadyCompleted(false);

    try {
      const trackedLink = await CheckoutLinkTrackingService.generate(planId, {
        studentId,
        qualificationId,
      });
      await navigator.clipboard.writeText(trackedLink.url);
      setCheckoutUrl(trackedLink.url);
      setIsCopied(true);
      window.setTimeout(() => setIsCopied(false), 2500);
    } catch (error) {
      if (error instanceof Error && error.message === CHECKOUT_ALREADY_COMPLETED_MESSAGE) {
        setCheckoutAlreadyCompleted(true);
      } else {
        alert(error instanceof Error ? error.message : 'Não foi possível copiar o link de checkout.');
      }
    } finally {
      setIsCopying(false);
    }
  }, [isCopying, qualificationId, selectedPlanId, studentId]);

  if (!open) {
    return null;
  }

  const selectedPlan = plans.find((plan) => String(plan.id) === selectedPlanId) ?? null;
  const selectedPlanCycle = selectedPlan?.billing_cycle.trim().toLowerCase() ?? '';
  const selectedPlanCycleLabel = selectedPlan
    ? CYCLE_LABELS[selectedPlanCycle] ?? CYCLE_LABELS[selectedPlan.billing_cycle] ?? selectedPlan.billing_cycle
    : '';
  const copyIconName = isCopying ? 'progress_activity' : isCopied ? 'done' : 'content_copy';

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
        <div className={styles['icon-circle']}>
          <span className="material-symbols-outlined">shopping_cart</span>
        </div>

        <h3 className={styles.title}>Link de checkout</h3>
        <p className={styles.message}>
          Selecione o plano de consultoria para gerar o link de checkout
          {leadName ? ` de ${leadName}` : ''}.
        </p>

        {checkoutAlreadyCompleted && (
          <div className={styles.notice} role="status">
            <span className="material-symbols-outlined">info</span>
            <p>{CHECKOUT_ALREADY_COMPLETED_MESSAGE}</p>
          </div>
        )}

        <div className={styles['select-wrap']}>
          {plansLoading ? (
            <Loading />
          ) : (
            <FormSelect
              id="commercial-checkout-plan"
              label="Plano de assinatura"
              options={plans.map((plan) => ({ value: String(plan.id), label: plan.name }))}
              value={selectedPlanId}
              onChange={(event) => setSelectedPlanId(event.target.value)}
              disabled={plans.length === 0}
              {...createPlanSelectFormatters(plans)}
            />
          )}
        </div>

        {selectedPlan && (
          <div className={styles['plan-summary']}>
            <div className={styles['plan-summary-row']}>
              <span className={styles['plan-summary-label']}>Valor</span>
              <span className={styles['plan-summary-value']}>
                {formatCurrency(selectedPlan.price_cents / 100)}
                {selectedPlanCycleLabel ? `/${selectedPlanCycleLabel.toLowerCase()}` : ''}
              </span>
            </div>
            <div className={styles['plan-summary-row']}>
              <span className={styles['plan-summary-label']}>Ciclo</span>
              <span className={styles['plan-summary-value']}>{selectedPlanCycleLabel || '—'}</span>
            </div>
            <div className={styles['plan-summary-row']}>
              <span className={styles['plan-summary-label']}>À vista</span>
              <span className={styles['plan-summary-value']}>
                {selectedPlan.is_single_payment ? 'Sim' : 'Não'}
              </span>
            </div>
          </div>
        )}

        {selectedPlanId && (
          <div className={styles['url-wrap']}>
            <FormInput
              id="commercial-checkout-url"
              label="URL do checkout"
              value={isCopying ? 'Gerando link...' : checkoutUrl || 'Clique no ícone para gerar e copiar'}
              readOnly
              loading={isCopying}
              endAdornment={(
                <Tooltip content="Copiar link de checkout">
                  <button
                    type="button"
                    className={styles['copy-btn']}
                    onClick={() => {
                      void handleCopy();
                    }}
                    aria-label="Copiar link de checkout"
                    disabled={isCopying}
                  >
                    <span
                      className={`material-symbols-outlined ${isCopying ? styles.spinner : ''}`.trim()}
                    >
                      {copyIconName}
                    </span>
                  </button>
                </Tooltip>
              )}
            />
          </div>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.btn} ${styles['btn-close']}`}
            onClick={onClose}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommercialCheckoutLinkModal;
